import { useAppDispatch } from '@user/store/hooks'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import {
  FieldState,
  getOrderByFunc,
  orderByToSortModel,
} from '@user/components/xGrid'
import { getSaveListViewPreferenceFunc } from '@user/store/userPreferences'
import { useSaveFilters } from './useSaveFilters'
import { setPage, setPerPage } from '@user/store/scWebHooks'

interface Props {
  columns: GridColDef[]
  orderBy: string
}

const saveListViewPreference = getSaveListViewPreferenceFunc('scWebhooks')
const getOrderBy = getOrderByFunc('email desc')

export const useActions = (props: Props) => {
  const dispatch = useAppDispatch()

  const { orderBy } = props

  const saveFilters = useSaveFilters()

  const handlePaginationModelChange = useCallback(
    (pageModel: GridPaginationModel) => {
      const { page, pageSize } = pageModel
      dispatch(setPerPage(pageSize))
      dispatch(setPage(page + 1))
    },
    [dispatch],
  )

  const sortModel = useMemo(
    () =>
      props.columns.length === 0 ? undefined : orderByToSortModel(orderBy),
    [orderBy, props.columns.length],
  )

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      const newOrderBy = getOrderBy(sortModel)
      if (newOrderBy != orderBy) saveFilters(newOrderBy)
    },
    [orderBy, saveFilters],
  )

  const onColumnWidthChange = useCallback(
    (
      columnData: GridColDef,
      columns: GridColDef[],
      columnVisibility: GridColumnVisibilityModel,
    ) => {
      const mapToFieldState: FieldState[] = [...columns]?.map(
        (row: GridColDef) => {
          if (row?.width) {
            return {
              field: row?.field,
              hide: !columnVisibility?.[row?.field],
              width: row?.width?.toString(),
            }
          }
          return {
            field: row?.field,
            hide: !columnVisibility?.[row?.field],
          }
        },
      )

      const foundColumn = mapToFieldState?.find(
        (row: FieldState) => row?.field == columnData?.field,
      )
      if (foundColumn) {
        const indexOf = mapToFieldState?.findIndex(
          (row) => row?.field == columnData?.field,
        )
        const newColumnObject = {
          ...foundColumn,
          width: columnData?.width?.toString(),
        }
        mapToFieldState.splice(indexOf, 1, newColumnObject)

        saveListViewPreference(dispatch, mapToFieldState)
      }
    },
    [dispatch],
  )

  const onSaveColumnState = useCallback(
    (columnOrder: FieldState[], columns: GridColDef[]) => {
      const mappedData = columnOrder?.map((row: FieldState) => {
        const found = columns?.find(
          (findRow: GridColDef) => findRow?.field == row?.field,
        )
        if (found && found?.width) {
          return {
            ...row,
            width: found?.width?.toString(),
          }
        }
        return {
          ...row,
        }
      })
      saveListViewPreference(dispatch, mappedData)
    },
    [dispatch],
  )

  return {
    onColumnWidthChange,
    onSaveColumnState,
    onSortModelChange,
    sortModel,
    handlePaginationModelChange,
  }
}
